import axios from "axios";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  RefreshCw,
  Users,
  UserCheck,
  UserX,
  Loader2,
  User,
  Hash,
  Mail,
  Phone,
  Calendar,
  CheckCircle,
  Search,
  Car,
  Wrench,
  Fuel,
  MapPin,
  Plus,
  Edit,
  Trash2,
} from "lucide-react";

import { BASE_URL } from "../../Screens/Settings/DASHBOARD_SETTINGS";
import { Button } from "../Global/UI/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../Global/UI/Dialog";
import { Input } from "../Global/UI/Input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../Global/UI/table";
import { Badge } from "../Global/UI/Badge";

const DisplayDrivers = () => {
  const [ambulances, setAmbulances] = useState([]);
  const [selectedAmbulance, setSelectedAmbulance] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newAmbulance, setNewAmbulance] = useState({
    username: "",
    password: "",
    registrationNumber: "",
    vehicleType: "ambulance",
    make: "",
    model: "",
    year: "",
    capacity: 1,
  });

  const history = useHistory();

  useEffect(() => {
    fetchAmbulances();
  }, []);

  const fetchAmbulances = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/drivers/getDrivers`);
      if (response.data.success) {
        setAmbulances(response.data.drivers);
      } else {
        toast.error(response.data.error || "Failed to fetch ambulances.");
      }
    } catch (error) {
      toast.error("Error fetching ambulances.");
      console.error("Error fetching ambulances:", error);
    }
  };

  // Filter ambulances based on search term
  const filteredAmbulances = ambulances.filter(
    (ambulance) =>
      ambulance.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ambulance.registrationNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ambulance.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ambulance.model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addAmbulance = async () => {
    try {
      const response = await axios.post(`${BASE_URL}/api/drivers/register`, newAmbulance);
      if (response.data.username) {
        toast.success("Ambulance added successfully!");
        setNewAmbulance({
          username: "",
          password: "",
          registrationNumber: "",
          vehicleType: "ambulance",
          make: "",
          model: "",
          year: "",
          capacity: 1,
        });
        setShowAddDialog(false);
        fetchAmbulances();
      } else {
        toast.error("Failed to add ambulance.");
      }
    } catch (error) {
      toast.error("Error adding ambulance.");
      console.error("Error adding ambulance:", error);
    }
  };

  const updateAmbulance = async () => {
    try {
      const response = await axios.post(`${BASE_URL}/api/drivers/update-driver/${selectedAmbulance._id}`, selectedAmbulance);
      if (response.data.success) {
        toast.success("Ambulance updated successfully!");
        setShowEditDialog(false);
        fetchAmbulances();
      } else {
        toast.error("Failed to update ambulance.");
      }
    } catch (error) {
      toast.error("Error updating ambulance.");
      console.error("Error updating ambulance:", error);
    }
  };

  const deleteAmbulance = async () => {
    try {
      const response = await axios.post(`${BASE_URL}/api/drivers/delete`, {
        ids: [selectedAmbulance._id],
      });
      if (response.data.success) {
        toast.success("Ambulance deleted successfully!");
        setShowDeleteDialog(false);
        fetchAmbulances();
      } else {
        toast.error("Failed to delete ambulance.");
      }
    } catch (error) {
      toast.error("Error deleting ambulance.");
      console.error("Error deleting ambulance:", error);
    }
  };

  // Calculate stats
  const availableAmbulances = filteredAmbulances.filter(
    (ambulance) => ambulance.status === 'available'
  ).length;
  const inUseAmbulances = filteredAmbulances.filter(
    (ambulance) => ambulance.status === 'in-use'
  ).length;
  const maintenanceAmbulances = filteredAmbulances.filter(
    (ambulance) => ambulance.status === 'maintenance'
  ).length;
  const outOfServiceAmbulances = filteredAmbulances.filter(
    (ambulance) => ambulance.status === 'out-of-service'
  ).length;

  return (
    <div>
      <div className="max-w-7xl mx-auto py-10">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Ambulances
            </h1>
            <p className="text-gray-600 text-sm">
              Manage your ambulance fleet
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => setShowAddDialog(true)}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Ambulance
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="flex items-center gap-6 mb-6 text-sm">
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4 text-gray-500" />
            <span className="font-medium">{filteredAmbulances.length}</span>
            <span className="text-gray-500">Total Ambulances</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="font-medium text-green-600">{availableAmbulances}</span>
            <span className="text-gray-500">Available</span>
          </div>
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-blue-600" />
            <span className="font-medium text-blue-600">{inUseAmbulances}</span>
            <span className="text-gray-500">In Use</span>
          </div>
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-orange-600" />
            <span className="font-medium text-orange-600">{maintenanceAmbulances}</span>
            <span className="text-gray-500">Maintenance</span>
          </div>
          <div className="flex items-center gap-2">
            <UserX className="w-4 h-4 text-red-600" />
            <span className="font-medium text-red-600">{outOfServiceAmbulances}</span>
            <span className="text-gray-500">Out of Service</span>
          </div>
        </div>

        {/* Search */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search ambulances..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Ambulances Table */}
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-48">Driver</TableHead>
                <TableHead className="w-32">Registration</TableHead>
                <TableHead className="w-32">Vehicle</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAmbulances.map((ambulance) => (
                <TableRow key={ambulance.driverId}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{ambulance.username}</div>
                      <div className="text-sm text-gray-500">
                        ID: {ambulance._id}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {ambulance.registrationNumber || "N/A"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {ambulance.make && ambulance.model
                        ? `${ambulance.make} ${ambulance.model} ${ambulance.year || ""}`
                        : "N/A"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      style={
                        ambulance.status === 'available'
                          ? { backgroundColor: '#10B981', color: 'white', border: 'none' }
                          : ambulance.status === 'in-use'
                          ? { backgroundColor: '#3B82F6', color: 'white', border: 'none' }
                          : ambulance.status === 'maintenance'
                          ? { backgroundColor: '#F59E0B', color: 'white', border: 'none' }
                          : { backgroundColor: '#EF4444', color: 'white', border: 'none' }
                      }
                    >
                      {ambulance.status ? ambulance.status.charAt(0).toUpperCase() + ambulance.status.slice(1) : "Unknown"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        history.push({
                          pathname: `/drivers/${ambulance._id}`,
                          state: { driver: ambulance },
                        });
                      }}
                      className="hover:bg-gray-100"
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredAmbulances.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No ambulances found.
            </div>
          )}
        </div>

        {/* Add Ambulance Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Ambulance</DialogTitle>
              <DialogDescription>
                Enter the details of the new ambulance.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <Input
                placeholder="Username"
                value={newAmbulance.username}
                onChange={(e) =>
                  setNewAmbulance({ ...newAmbulance, username: e.target.value })
                }
              />
              <Input
                placeholder="Password"
                type="password"
                value={newAmbulance.password}
                onChange={(e) =>
                  setNewAmbulance({ ...newAmbulance, password: e.target.value })
                }
              />
              <Input
                placeholder="Registration Number"
                value={newAmbulance.registrationNumber}
                onChange={(e) =>
                  setNewAmbulance({ ...newAmbulance, registrationNumber: e.target.value })
                }
              />
              <Input
                placeholder="Make"
                value={newAmbulance.make}
                onChange={(e) =>
                  setNewAmbulance({ ...newAmbulance, make: e.target.value })
                }
              />
              <Input
                placeholder="Model"
                value={newAmbulance.model}
                onChange={(e) =>
                  setNewAmbulance({ ...newAmbulance, model: e.target.value })
                }
              />
              <Input
                placeholder="Year"
                value={newAmbulance.year}
                onChange={(e) =>
                  setNewAmbulance({ ...newAmbulance, year: e.target.value })
                }
              />
            </div>
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => setShowAddDialog(false)}
              >
                Cancel
              </Button>
              <Button onClick={addAmbulance}>Add Ambulance</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Ambulance Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Ambulance</DialogTitle>
              <DialogDescription>
                Update the ambulance details below.
              </DialogDescription>
            </DialogHeader>
            {selectedAmbulance && (
              <div className="space-y-4">
                <Input
                  placeholder="Username"
                  value={selectedAmbulance.username}
                  onChange={(e) =>
                    setSelectedAmbulance({ ...selectedAmbulance, username: e.target.value })
                  }
                />
                <Input
                  placeholder="Registration Number"
                  value={selectedAmbulance.registrationNumber || ""}
                  onChange={(e) =>
                    setSelectedAmbulance({ ...selectedAmbulance, registrationNumber: e.target.value })
                  }
                />
                <Input
                  placeholder="Make"
                  value={selectedAmbulance.make || ""}
                  onChange={(e) =>
                    setSelectedAmbulance({ ...selectedAmbulance, make: e.target.value })
                  }
                />
                <Input
                  placeholder="Model"
                  value={selectedAmbulance.model || ""}
                  onChange={(e) =>
                    setSelectedAmbulance({ ...selectedAmbulance, model: e.target.value })
                  }
                />
                <Input
                  placeholder="Year"
                  value={selectedAmbulance.year || ""}
                  onChange={(e) =>
                    setSelectedAmbulance({ ...selectedAmbulance, year: e.target.value })
                  }
                />
              </div>
            )}
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => setShowEditDialog(false)}
              >
                Cancel
              </Button>
              <Button onClick={updateAmbulance}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete the ambulance for {selectedAmbulance?.username}?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => setShowDeleteDialog(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={deleteAmbulance}>
                Confirm Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
};

export default DisplayDrivers;